import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { ArrowRightShort, ChevronDown, ChevronUp } from 'react-bootstrap-icons';

import useTranslation from '../../../hooks/use_translation';
import BtnIcon from '../buttons/btn_icon';

const PageHeader = ({
	align = 'left',
	children,
	className,
	excerpt,
	excerptClassName,
	headingType = 'h1',
	linkType,
	pageVariant = 'main_page',
	resetSpacing = false,
	subHeadingType = 'p',
	subTitle,
	title,
	to,
	variant = 'light',
	withButton = false,
	...props
}) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const [isOverflowing, setIsOverflowing] = useState(false);
	const excerptRef = useRef(null);
	const { t } = useTranslation('component_page_header');

	const maxHeight = 80;

	const checkOverflow = () => {
		if (excerptRef.current) {
			setIsOverflowing(excerptRef.current.scrollHeight > maxHeight);
		}
	};

	useEffect(() => {
		const observer = new ResizeObserver(() => {
			checkOverflow();
		});

		if (excerptRef.current) {
			observer.observe(excerptRef.current);
		}

		return () => {
			if (excerptRef.current) {
				observer.unobserve(excerptRef.current);
			}
		};
	}, []);

	useEffect(() => {
		checkOverflow();
	}, [excerpt]);

	const toggleExcerpt = () => {
		setIsExpanded((prev) => !prev);
	};

	const HeadingComponent = headingType;
	const SubHeadingComponent = subHeadingType;

	const aligns = {
		center: 'text-center',
		left: '',
		right: 'text-end',
	};

	const pageVariants = {
		center: {
			btnIcon: 'xl',
			excerpt: 'large text-center',
			spacing: 'my-0',
			subTitle: 'lead text-center',
			title: 'title-xl text-center',
		},
		hero: {
			btnIcon: 'xl',
			excerpt: 'large',
			spacing: 'my-5',
			subTitle: 'lead',
			title: 'title-large text-center',
		},
		list_page: {
			btnIcon: 'md',
			excerpt: 'xs',
			spacing: 'mb-4',
			subTitle: 'base',
			title: 'title-medium',
		},

		main_page: {
			btnIcon: 'xl',
			excerpt: 'sm',
			spacing: 'my-5',
			subTitle: 'lead',
			title: 'title-xl',
		},
		nested_page: {
			btnIcon: 'lg',
			excerpt: 'sm',
			spacing: 'mb-5',
			subTitle: 'lead',
			title: 'title-large',
		},
		small: {
			btnIcon: 'lg',
			excerpt: 'sm',
			spacing: 'mb-5',
			subTitle: 'lead ',
			title: 'lead',
		},
	};

	const variants = {
		dark: {
			btnIcon: 'white',
			excerpt: 'text-white o-80',
			subTitle: 'text-white o-50',
			title: 'text-white',
		},
		light: {
			btnIcon: 'dark',
			excerpt: 'muted',
			subTitle: 'muted',
			title: 'text-dark',
		},
		primary_light: {
			btnIcon: 'white',
			excerpt: 'text-white o-80',
			subTitle: 'text-white o-50',
			title: 'text-primary-light',
		},
	};

	return (
		<>
			<div
				className={`page-header ${
					!resetSpacing && `${pageVariants[pageVariant].spacing}`
				} ${className}`}
			>
				<div>
					{subTitle && (
						<SubHeadingComponent
							className={`text-muted mb-0 ${pageVariants[pageVariant].subTitle} ${variants[variant].subTitle} ${aligns[align]}`}
						>
							{subTitle}
						</SubHeadingComponent>
					)}

					<HeadingComponent
						className={`mb-0 me-2 ${variants[variant].title === 'dark' ? 'page-header-link' : ''}  ${variants[variant].title} ${pageVariants[pageVariant].title} ${aligns[align]}`}
					>
						{title}
					</HeadingComponent>

					{excerpt && (
						<div className="mt-3">
							<div
								className={`${pageVariants[pageVariant].excerpt} ${variants[variant].excerpt} ${excerptClassName} excerpt ${isOverflowing && !isExpanded ? 'fade-content' : ''} ${aligns[align]}`}
								ref={excerptRef}
								style={{
									height:
										isOverflowing && !isExpanded ? `${maxHeight}px` : 'auto',
									maxHeight:
										isOverflowing && !isExpanded ? `${maxHeight}px` : 'none',
									overflow: 'hidden',
									position: 'relative',
								}}
							>
								{excerpt}
							</div>
							{isOverflowing && (
								<>
									<div className="d-flex justify-content-end">
										<button
											className="btn btn-link text-dark xs mt-2"
											onClick={toggleExcerpt}
											style={{ textDecoration: 'none' }}
										>
											{isExpanded ? (
												<div className="d-flex align-items-center gap-1">
													{t('t2')}
													<ChevronUp />
												</div>
											) : (
												<div className="d-flex align-items-center gap-1">
													{t('t1')}
													<ChevronDown />
												</div>
											)}
										</button>
									</div>
									<hr />
								</>
							)}
						</div>
					)}
				</div>

				{withButton && (
					<BtnIcon
						iconSize={pageVariants[pageVariant].btnIcon}
						linkType={linkType}
						to={to}
						variant={variants[variant].btnIcon}
						{...props}
					>
						<ArrowRightShort />
					</BtnIcon>
				)}
				{children}
			</div>
		</>
	);
};

PageHeader.propTypes = {
	align: PropTypes.oneOf(['center', 'left', 'right']),
	children: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
	className: PropTypes.string,
	excerpt: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
	excerptClassName: PropTypes.string,
	headingType: PropTypes.string,
	linkType: PropTypes.string,
	pageVariant: PropTypes.string,
	resetSpacing: PropTypes.bool,
	size: PropTypes.string,
	subHeadingType: PropTypes.string,
	subTitle: PropTypes.string,
	title: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
	to: PropTypes.string,
	variant: PropTypes.string,
	withButton: PropTypes.bool,
};

export default PageHeader;
